import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  AppState,
  AuthSelectors,
  DevicesSelectors,
  InstallationsSelectors,
  RemoteConfigSelectors,
  RichDevice,
} from '@store';

import {
  InstallationItem,
  InstallationItemGatewayDeviceFull,
  ViError,
} from '../../../projects/client';
import { AssistantState } from '../assistant';
import {
  ServiceTicketErrorCodes,
  ServiceTicket,
  ServiceTicketArrangedCallback,
  ServiceTicketCallType,
  ServiceTicketError,
  ServiceTicketErrorDetails,
  ServiceTicketRoutingProvider,
  ServiceTicketBoilerSerialErrors,
} from './service-ticket.model';
import { ServiceTicketState } from './service-ticket.state';
import { AssistantSearch } from '../../../projects/client/apis/assistant/assistant.api-model';
import { ServiceTicketHelper } from './service-ticket.helper';

export namespace ServiceTicketSelectors {
  export const serviceTicketState = (state: AppState): ServiceTicketState => state.serviceTicket;
  export const assistantState = (state: AppState): AssistantState => state.assistant;

  export const selectActivePage = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): ServiceTicket.ServiceTicketPage => state.activePage
  );

  export const selectErrorTicketDetails = createSelector(
    serviceTicketState,
    (
      state: ServiceTicketState
    ): {
      boilerSerial: string;
      errorCode: string[];
      otherErrorCode: string;
      additionalFeedback: string;
      phoneNumber: string;
      waitTime: number;
    } => {
      return {
        boilerSerial: state.createPageState.searchedSerialNumber,
        errorCode: state.createPageState.userErrorCodes,
        otherErrorCode: state.createPageState.userOtherErrorCodes,
        additionalFeedback: state.callPageState.feedback,
        phoneNumber: state.callPageState.phoneNumber,
        waitTime: state.callPageState.waitingTimeState.waitTime,
      };
    }
  );

  export const selectCallNowDetails = createSelector(
    serviceTicketState,
    (
      state: ServiceTicketState
    ): {
      callNumber: string;
    } => {
      return {
        callNumber: state.callPageState.callNowState.acdNumber,
      };
    }
  );

  export const selectArrangedCallbackDetails = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): ServiceTicketArrangedCallback => {
      return state.callPageState.arrangedCallState.arrangedCallback;
    }
  );

  export const selectArrangedCallbacks = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): ServiceTicketArrangedCallback[] => {
      return state.arrangedCallbacks;
    }
  );

  export const selectCallTypeInteractionDisabled = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): boolean => {
      return (
        state.callPageState.callNowTimeExpired !== true &&
        (state.callPageState.waitingTimeState.loading ||
          state.callPageState.waitingTimeState.loaded)
      );
    }
  );

  export const selectSearchedBoilerSerial = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): string => {
      return state.createPageState.searchedSerialNumber;
    }
  );

  export const selectIsBoilerSerialValid = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): boolean => {
      const serialPresent = !!state.createPageState.searchedSerialNumber;
      const regexCheckIfStringHasLetter = new RegExp(/[^0-9]+/);
      const serialWithoutLetters = !regexCheckIfStringHasLetter.test(
        state.createPageState.searchedSerialNumber
      );
      const serialValidated =
        state.createPageState.searchedSerialNumberValidationState.isValid &&
        !state.createPageState.searchedSerialNumberValidationState.loading;

      return serialPresent && serialWithoutLetters && serialValidated;
    }
  );

  export const selectDisplayBoilerSerialError = createSelector(
    serviceTicketState,
    ServiceTicketSelectors.selectIsBoilerSerialValid,
    (state: ServiceTicketState, isBoilerSerialValid): boolean => {
      return (
        !isBoilerSerialValid &&
        state.createPageState.searchedSerialNumberValidationState.loaded &&
        !state.createPageState.searchedSerialNumberValidationState.loading &&
        state.createPageState.searchedSerialNumberValidationState.isValid === false
      );
    }
  );

  export const selectDisplayBypassValidationCheckbox = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): boolean => {
      const serialPresent =
        state.createPageState.searchedSerialNumber != null &&
        state.createPageState.searchedSerialNumber !== '';
      const serialValidationFailed =
        state.createPageState.searchedSerialNumberValidationState.isValid === false;

      return serialPresent && serialValidationFailed;
    }
  );

  export const selectCreatePageAvailableSteps = createSelector(
    serviceTicketState,
    InstallationsSelectors.getLoadedIndicator(),
    (state: ServiceTicketState, installationsLoaded: boolean): string[] => {
      // 1. Step Init
      const canDisplayStepInit = !installationsLoaded;

      // 2. Step Enter Serial
      const canDisplayStepBoilerSerial = !canDisplayStepInit;

      // 2.2 Loader
      const canDisplayStepErrorCodeLoader =
        canDisplayStepBoilerSerial &&
        (state.createPageState.searchedSerialNumberValidationState.loading ||
          state.createPageState.deviceDetails.loading);

      // 3.1 Step Error Codes
      const _validationComplete =
        state.createPageState.searchedSerialNumber &&
        state.createPageState.searchedSerialNumberValidationState.isValid &&
        !state.createPageState.searchedSerialNumberValidationState.loading &&
        state.createPageState.searchedSerialNumberValidationState.loaded;
      const _errorsLoadingComplete = state.createPageState.deviceDetails.loaded;
      const canDisplayStepErrorCode =
        canDisplayStepBoilerSerial && _validationComplete && _errorsLoadingComplete;

      // 3.2 Step Other Error Code Field
      const canDisplayStepEnterOtherErrorCode =
        canDisplayStepErrorCode &&
        !state.createPageState.isNoErrorCode &&
        state.createPageState.otherErrorSelected;

      // 3.3 Step Display support button
      const canDisplayCallSupport = state.createPageState.isNoErrorCode;

      // 4. Step DetailedInformation
      const _errorCodeValuePresent =
        !state.createPageState.otherErrorSelected &&
        state.createPageState.userErrorCodes?.length > 0;
      const _otherErrorCodeValuePresent =
        state.createPageState.otherErrorSelected && !!state.createPageState.userOtherErrorCodes;
      const canDisplayStepDetailedInformation =
        !canDisplayCallSupport &&
        canDisplayStepBoilerSerial &&
        canDisplayStepErrorCode &&
        (_errorCodeValuePresent || _otherErrorCodeValuePresent);

      const canDisplayAdditionalArticlesLoader =
        canDisplayStepDetailedInformation && state.articlesPageState.searchedArticlesState.loading;

      const canDisplayAdditionalArticles =
        canDisplayStepDetailedInformation && state.articlesPageState.searchedArticlesState.loaded;

      const stepsToDisplay = [];

      if (canDisplayStepInit) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.Init);
      }

      if (canDisplayStepBoilerSerial) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.EnterSerial);
      }

      if (canDisplayStepErrorCodeLoader) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.EnterErrorCodeLoader);
      }

      if (canDisplayStepErrorCode) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.EnterErrorCode);
      }

      if (canDisplayStepEnterOtherErrorCode) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.EnterOtherErrorCode);
      }

      if (canDisplayCallSupport) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.CallSupport);
      }

      if (canDisplayStepDetailedInformation) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.DetailedInformation);
      }

      if (canDisplayAdditionalArticlesLoader) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.AdditionalArticlesLoader);
      }

      if (canDisplayAdditionalArticles) {
        stepsToDisplay.push(ServiceTicket.CreatePageSteps.AdditionalArticles);
      }

      return stepsToDisplay;
    }
  );

  export const selectCanRequestCallNow = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): boolean => {
      const canRequestCall =
        state.callPageState.callType === ServiceTicketCallType.CallNow &&
        !state.callPageState.waitingTimeState.loading &&
        !state.callPageState.waitingTimeState.loaded &&
        state.callPageState.callNowState.acdNumber === null &&
        !state.callPageState.callNowState.loading;

      return canRequestCall;
    }
  );

  export const selectCanRequestArrangedCallback = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): boolean => {
      const canRequestCall =
        state.callPageState.callType === ServiceTicketCallType.ArrangedCallback &&
        !state.callPageState.waitingTimeState.loading &&
        !state.callPageState.waitingTimeState.loaded &&
        state.callPageState.arrangedCallState.arrangedCallback === null &&
        !state.callPageState.arrangedCallState.loading &&
        !!state.callPageState.isValidPhoneNumber;

      return canRequestCall;
    }
  );

  export const selectCallPageAvailableSteps = createSelector(
    serviceTicketState,
    AuthSelectors.isCompanyFromGermany(),
    (state: ServiceTicketState, isCompanyFromGermany: boolean): string[] => {
      // 1. Step Init
      const canDisplayStepFeedback = true;

      const canDisplayStepCallType = isCompanyFromGermany
        ? canDisplayStepFeedback &&
          state.callPageState.feedback?.length >= 20 &&
          state.callPageState.feedbackUpdating === false &&
          state.createPageState.routingProviderState.loaded
        : canDisplayStepFeedback &&
          state.callPageState.feedback?.length >= 20 &&
          state.callPageState.feedbackUpdating === false;

      const canDisplayRoutingProviderLoading =
        state.callPageState.feedback?.length >= 20 &&
        state.createPageState.routingProviderState.loading;

      const _callTimeLoading =
        state.callPageState.waitingTimeState.loading ||
        state.callPageState.callNowState.loading ||
        state.callPageState.arrangedCallState.loading;
      const _secondRequestLoading =
        state.callPageState.waitingTimeState.loaded &&
        !!state.callPageState.waitingTimeState.waitTime &&
        //
        ((state.callPageState.callType === ServiceTicketCallType.CallNow &&
          !state.callPageState.callNowState.loaded) ||
          //
          (state.callPageState.callType === ServiceTicketCallType.ArrangedCallback &&
            !state.callPageState.arrangedCallState.loaded));
      const canDisplayStepRequestLoader =
        canDisplayStepCallType && (_callTimeLoading || _secondRequestLoading);

      const canDisplaySteplResultCallNow =
        canDisplayStepCallType &&
        state.callPageState.callNowState.loaded &&
        state.callPageState.callType == ServiceTicketCallType.CallNow;

      const canDisplaySteplResultArrangedCallback =
        canDisplayStepCallType &&
        state.callPageState.arrangedCallState.loaded &&
        state.callPageState.callType == ServiceTicketCallType.ArrangedCallback;

      const stepsToDisplay = [];

      if (canDisplayStepFeedback) {
        stepsToDisplay.push(ServiceTicket.CallPageSteps.Feedback);
      }

      if (canDisplayRoutingProviderLoading) {
        stepsToDisplay.push(ServiceTicket.CallPageSteps.RoutingProviderLoader);
      }

      if (canDisplayStepCallType) {
        stepsToDisplay.push(ServiceTicket.CallPageSteps.CallType);
      }

      if (canDisplayStepRequestLoader) {
        stepsToDisplay.push(ServiceTicket.CallPageSteps.RequestLoader);
      }

      if (canDisplaySteplResultCallNow) {
        stepsToDisplay.push(ServiceTicket.CallPageSteps.ResultCallNow);
      }

      if (canDisplaySteplResultArrangedCallback) {
        stepsToDisplay.push(ServiceTicket.CallPageSteps.ResultArrangedCallback);
      }

      return stepsToDisplay;
    }
  );

  export const selectRoutingProvider = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): string => {
      return state.createPageState.routingProviderState.routingProvider;
    }
  );

  export const selectSearchSerialNumber = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): string => {
      return state.createPageState.searchedSerialNumber;
    }
  );

  export const selectIsRoutingProviderError = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): boolean => {
      return !!state.createPageState.routingProviderState.error;
    }
  );

  export const selectRequestRoutingProviderPayloadBoilerSerial = createSelector(
    selectSearchSerialNumber,
    AuthSelectors.isCompanyFromGermany(),
    (boilerSerial: string, isCompanyFromGermany: boolean): string => {
      return isCompanyFromGermany ? boilerSerial : undefined;
    }
  );

  export function selectBoilerSerialAutocompleteDevices(
    installationId?: string
  ): MemoizedSelector<
    AppState,
    InstallationItemGatewayDeviceFull[],
    DefaultProjectorFn<InstallationItemGatewayDeviceFull[]>
  > {
    return createSelector(
      serviceTicketState,
      InstallationsSelectors.selectInstallations(),
      DevicesSelectors.getDevicesOfSelectedGateway(),
      (
        state: ServiceTicketState,
        allInstallations: InstallationItem[],
        selectedGatewayDevices: RichDevice[]
      ): InstallationItemGatewayDeviceFull[] => {
        const boilerSerialSearchText = state.createPageState.searchedSerialNumber;

        const items: InstallationItemGatewayDeviceFull[] = [];

        // Find all devices on all installations and gateways
        allInstallations.forEach((installation) => {
          installation.gateways.forEach((gateway) => {
            gateway.devices.forEach((device) => {
              // !mutation from legacy code!
              // if possible, populate marketing name with device short name
              if (!device.marketingName) {
                const gwDevice = selectedGatewayDevices?.find(
                  (deviceGw) => deviceGw.boilerSerial === device.serial
                );
                device.marketingName = gwDevice?.shortDeviceName || '';
              }
              items.push({
                ...device,
                marketingName: device.marketingName,
                installationId: installation.id,
                serial: device.serial,
                ownerName: installation.ownerName,
              });
            });
          });
        });

        // filter out devices which are not product
        const filteredItems = items
          .filter((entry) => {
            return !!entry.serial;
          })
          // comment out if we need filtering based on roles
          // .filter(
          //   (entry) =>
          //     entry.roles.some((role) => role.includes(DeviceRoles.GenericProduct))
          // )
          .filter((entry) => {
            // filter in installation details cascade context
            const isInstallationDetailsPage = !!installationId;
            if (isInstallationDetailsPage && entry.installationId !== +installationId) {
              return false;
            }

            // if search phrase is present, filter out all other devices
            if (!isInstallationDetailsPage && boilerSerialSearchText?.length > 0) {
              return entry.serial.startsWith(boilerSerialSearchText);
            } else {
              return true;
            }
          });

        return filteredItems;
      }
    );
  }

  export const selectAllBoilerDeviceErrorDetails = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): ServiceTicketErrorDetails => {
      return state.createPageState.deviceDetails?.errorsDetails;
    }
  );

  export const selectAssistantArticlesSummary = createSelector(
    serviceTicketState,
    assistantState,
    (serviceTicketState: ServiceTicketState, assistantState: AssistantState): AssistantSearch[] => {
      if (
        serviceTicketState.articlesPageState.searchedArticlesState.loaded &&
        assistantState.searchDocuments.loaded
      ) {
        const articles = assistantState.searchDocuments?.value;

        if (articles?.length > 0) {
          return articles;
        } else {
          return [];
        }
      } else {
        return null;
      }
    }
  );

  export const selectDisplayArticleDetails = createSelector(
    assistantState,
    (state: AssistantState): boolean => {
      return !!state.selectedDocument.searchDocument;
    }
  );

  export const selectArticleFeedbackDetails = createSelector(
    serviceTicketState,
    (
      state: ServiceTicketState
    ): {
      serial: string;
      productName: string;
      errorCode: string[];
      otherErrorCode: string;
    } => {
      const productName = !!state.createPageState.searchedSerialMarketingName
        ? state.createPageState.searchedSerialMarketingName
        : state.createPageState.searchedSerialNumber;

      return {
        serial: state.createPageState.searchedSerialNumber,
        productName: productName,
        errorCode: state.createPageState.userErrorCodes,
        otherErrorCode: state.createPageState.userOtherErrorCodes,
      };
    }
  );

  export const selectErrorCodes = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): ServiceTicketErrorCodes => {
      return {
        errorCode: state.createPageState.userErrorCodes,
        otherErrorCode: state.createPageState.userOtherErrorCodes,
        otherErrorSelected: state.createPageState.otherErrorSelected,
      };
    }
  );

  export const selectBoilerDeviceErrorDetails = createSelector(
    serviceTicketState,
    ServiceTicketSelectors.selectErrorCodes,
    (
      state: ServiceTicketState,
      errorCodes: {
        errorCode: string[];
        otherErrorCode: string;
      }
    ): ServiceTicketErrorDetails => {
      const detailsFiltered =
        state.createPageState.deviceDetails?.errorsDetails?.details?.filter((entry) => {
          return [...errorCodes.errorCode, errorCodes.otherErrorCode].includes(entry.faultCode);
        }) || [];

      const errorCodeFromInput = errorCodes.otherErrorCode;

      if (errorCodeFromInput) {
        const matchingError = state.createPageState.allFetchedErrors.find(
          (error) => error.faultCode === errorCodeFromInput
        );
        if (matchingError) {
          const mappedError: ServiceTicketError = {
            ...matchingError,
            text: ServiceTicketHelper.faultCodeTextMapper(matchingError),
          };
          detailsFiltered.push(mappedError);
        }
      }

      return {
        detailsInDifferentLanguage:
          state.createPageState.deviceDetails?.errorsDetails?.detailsInDifferentLanguage,
        details: detailsFiltered,
      };
    }
  );

  export const selectIsAllowedToUseCallNowByVkorg = createSelector(
    RemoteConfigSelectors.selectServiceTicketCallNowBlacklist,
    AuthSelectors.selectPrimaryVkorg,
    (blacklist: string[], vkorg: string): boolean => {
      return !blacklist.some((ignoredVkorg) => vkorg.includes(ignoredVkorg));
    }
  );

  export const selectIsSalesforceWithGermanyCompany = createSelector(
    selectIsAllowedToUseCallNowByVkorg,
    selectRoutingProvider,
    selectIsRoutingProviderError,
    AuthSelectors.isCompanyFromGermany(),
    (
      isAllowedToUseCallNowByVkorg: boolean,
      routingProvider: string,
      isRoutingProviderError: boolean,
      isCompanyFromGermany: boolean
    ): boolean => {
      return (
        isAllowedToUseCallNowByVkorg &&
        routingProvider === ServiceTicketRoutingProvider.Salesforce &&
        !isRoutingProviderError &&
        isCompanyFromGermany
      );
    }
  );

  export const selectBoilerSerialError = createSelector(
    serviceTicketState,
    (state: ServiceTicketState): ViError | ServiceTicketBoilerSerialErrors =>
      state.createPageState.searchedSerialNumberValidationState.error
  );
}
